import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';

// =============================================================================
// ANTLR (Observability Search) → Lezer (QueryBuilder) query converter
// =============================================================================
//
// The reverse of the metrics-side `parsedQueryToAntlr` serializer. Converts an
// ANTLR query string back into the two-colon filter format (field:op:value)
// consumed by the QueryBuilder.
//
// Supported conversions:
//   field=value         →  field:=:value
//   field!=value        →  field:!=:value
//   field>value         →  field:>:value  (>=, <, <= analogous)
//   NOT field=value     →  NOT field:=:value
//   AND / OR            →  AND / OR
//   (…)                 →  (…) (inner expression converted recursively)
//   any_span(…) / no_span(…) etc.
//                       →  anySpan(…) / noSpan(…) (typed expression)
//   field="*v*"         →  field:**:v
//   field="v*"          →  field:^=:v
//   field="*v"          →  field:=^:v
//   field="/regex/"     →  field://:/regex/
//   field!="/regex/"    →  field:!//:/regex/
//   field EXISTS        →  field:=:__present__
//   field NOT EXISTS    →  field:!=:__present__
//
// =============================================================================

/** Quote a Lezer filter value when it contains characters outside the bare-value class. */
function quoteLezerValue(v: string): string {
  // Empty must be `""` so `field:=:""` round-trips through FILTER_RE; a bare
  // `field:=:` is free text and breaks paste of antlr `field=""`.
  if (v === '') {
    return '""';
  }
  // Lezer bare values may not contain whitespace, comma, colon, quote, or parens.
  return /[\s,:"()]/.test(v) ? `"${v}"` : v;
}

const ANTLR_QUOTE_CHARS = ['"', "'", '`'];

function isAntlrQuote(c: string | undefined): c is string {
  return c !== undefined && ANTLR_QUOTE_CHARS.includes(c);
}

const ANTLR_VALUE_BOUNDARY_RE = /[\s=><:!()]/;

function opensAntlrQuote(text: string, i: number): boolean {
  const c = text[i];
  if (c === '"') {
    return true;
  }
  return isAntlrQuote(c) && (i === 0 || ANTLR_VALUE_BOUNDARY_RE.test(text[i - 1]));
}

function parseQuotedAntlrString(antlr: string, start: number): { value: string; end: number } | null {
  const quote = antlr[start];
  if (!isAntlrQuote(quote)) {
    return null;
  }
  let j = start + 1;
  let content = '';
  while (j < antlr.length) {
    if (antlr[j] === '\\' && j + 1 < antlr.length) {
      const next = antlr[j + 1];
      content += next === quote ? quote : next === '\\' ? '\\' : next;
      j += 2;
    } else if (antlr[j] === quote) {
      return { value: content, end: j + 1 };
    } else {
      content += antlr[j++];
    }
  }
  return null;
}

function splitOrSeparatedValues(inner: string): string[] | null {
  const values: string[] = [];
  let i = 0;
  while (i < inner.length) {
    while (i < inner.length && /\s/.test(inner[i])) {
      i++;
    }
    if (i >= inner.length) {
      break;
    }

    let value: string;
    if (isAntlrQuote(inner[i])) {
      const quoted = parseQuotedAntlrString(inner, i);
      if (!quoted) {
        return null;
      }
      value = quoted.value;
      i = quoted.end;
    } else {
      const end = inner.slice(i).search(/[\s()]/);
      const raw = end === -1 ? inner.slice(i) : inner.slice(i, i + end);
      if (!raw || /[=<>]/.test(raw)) {
        return null;
      }
      value = raw;
      i += raw.length;
    }
    values.push(value);

    while (i < inner.length && /\s/.test(inner[i])) {
      i++;
    }
    if (i >= inner.length) {
      break;
    }
    if (/^OR\b/i.test(inner.slice(i))) {
      i += 2;
      continue;
    }
    return null;
  }
  return values.length > 0 ? values : null;
}

function parseOneAntlrMembershipList(antlr: string, start: number): { lezer: string; end: number } | null {
  const m = /^([a-zA-Z0-9_.-]+)(!=|=)\(/.exec(antlr.slice(start));
  if (!m) {
    return null;
  }
  const field = m[1];
  const antlrOp = m[2];
  const innerStart = start + m[0].length;
  const groupEnd = findGroupEnd(antlr, innerStart);
  if (groupEnd <= innerStart || antlr[groupEnd - 1] !== ')') {
    return null;
  }
  const values = splitOrSeparatedValues(antlr.slice(innerStart, groupEnd - 1));
  if (!values) {
    return null;
  }
  return {
    lezer: `${field}:${antlrOp}:${values.map(quoteLezerValue).join(',')}`,
    end: groupEnd,
  };
}

const LEZER_EQ_FILTER_RE = /^([a-zA-Z0-9_.-]+):(!=|=):(.+)$/;

function tryCollapseDistributedMultiValue(inner: string): string | null {
  const trimmed = inner.trim();
  if (!trimmed) {
    return null;
  }

  const tryJoin = (join: 'OR' | 'AND', expectedOp: '=' | '!='): string | null => {
    const parts = splitTopLevelBoolean(trimmed, join);
    if (!parts || parts.length < 2) {
      return null;
    }
    let field: string | undefined;
    const values: string[] = [];
    for (const part of parts) {
      const parsed = parseOneAntlrFilter(part.trim(), 0);
      if (!parsed || parsed.end !== part.trim().length) {
        return null;
      }
      const m = LEZER_EQ_FILTER_RE.exec(parsed.lezer);
      if (!m || m[2] !== expectedOp) {
        return null;
      }
      if (field === undefined) {
        field = m[1];
      } else if (field !== m[1]) {
        return null;
      }
      values.push(m[3]);
    }
    return `${field}:${expectedOp}:${values.join(',')}`;
  };

  return tryJoin('OR', '=') ?? tryJoin('AND', '!=');
}

function splitTopLevelBoolean(text: string, join: 'AND' | 'OR'): string[] | null {
  const parts: string[] = [];
  let depth = 0;
  let openQuote: string | null = null;
  let start = 0;
  const joinLen = join.length;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (openQuote) {
      if (c === '\\' && i + 1 < text.length) {
        i++;
      } else if (c === openQuote) {
        openQuote = null;
      }
      continue;
    }
    if (opensAntlrQuote(text, i)) {
      openQuote = c;
      continue;
    }
    if (c === '(') {
      depth++;
      continue;
    }
    if (c === ')') {
      depth--;
      continue;
    }
    if (depth !== 0) {
      continue;
    }
    if (
      text.slice(i, i + joinLen).toUpperCase() === join &&
      (i === 0 || /\s/.test(text[i - 1])) &&
      (i + joinLen >= text.length || /\s/.test(text[i + joinLen]))
    ) {
      parts.push(text.slice(start, i));
      i += joinLen - 1;
      start = i + 1;
    }
  }
  if (parts.length === 0) {
    return null;
  }
  parts.push(text.slice(start));
  return parts;
}

/**
 * Try to parse one ANTLR-style filter (`field[op]value`) starting at `start`.
 * Returns the equivalent Lezer token string and the end offset, or null if the
 * input at that position is not a filter.
 */
function parseOneAntlrFilter(antlr: string, start: number): { lezer: string; end: number } | null {
  const ANTLR_FILTER_RE = /^([a-zA-Z0-9_.-]+)(!=|>=|<=|>|<|=)/;
  const m = ANTLR_FILTER_RE.exec(antlr.slice(start));
  if (!m) {
    return null;
  }

  const field = m[1];
  const antlrOp = m[2];
  let pos = start + m[0].length;

  // Parse the value: either a quoted string (", ' or `) or bare non-whitespace chars.
  let inner: string;
  let wasQuoted = false;

  if (isAntlrQuote(antlr[pos])) {
    wasQuoted = true;
    const quoted = parseQuotedAntlrString(antlr, pos);
    if (!quoted) {
      return null;
    }
    inner = quoted.value;
    pos = quoted.end;
  } else {
    // Bare value ends at the next whitespace or parenthesis.
    const end = antlr.slice(pos).search(/[\s()]/);
    inner = end === -1 ? antlr.slice(pos) : antlr.slice(pos, pos + end);
    pos += inner.length;
    if (!inner) {
      return null;
    }
  }

  // Detect glob and regex patterns emitted by parsedQueryToAntlr (always in quoted strings).
  let lezerOp = antlrOp;
  let lezerVal = inner;

  // Regex form `field="/regex/"` (or `!=`) takes precedence — `/foo*/` would
  // otherwise be misclassified as a glob ends-with (=^) pattern.
  if (
    (antlrOp === '=' || antlrOp === '!=') &&
    wasQuoted &&
    inner.length > 1 &&
    inner.startsWith('/') &&
    inner.endsWith('/')
  ) {
    lezerOp = antlrOp === '=' ? '//' : '!//';
    // Keep the `/` delimiters — the Lezer side stores them as part of the value.
    lezerVal = inner;
  } else if (antlrOp === '=' && wasQuoted) {
    if (inner.startsWith('*') && inner.endsWith('*') && inner.length > 2) {
      lezerOp = '**';
      lezerVal = inner.slice(1, -1);
    } else if (inner.endsWith('*') && !inner.startsWith('*')) {
      lezerOp = '^=';
      lezerVal = inner.slice(0, -1);
    } else if (inner.startsWith('*') && !inner.endsWith('*')) {
      lezerOp = '=^';
      lezerVal = inner.slice(1);
    }
  } else if (antlrOp === '!=' && wasQuoted && inner.startsWith('*') && inner.endsWith('*') && inner.length > 2) {
    // Negated glob `field!="*v*"` is the "does not contain" operator.
    lezerOp = '!**';
    lezerVal = inner.slice(1, -1);
  }

  return { lezer: `${field}:${lezerOp}:${quoteLezerValue(lezerVal)}`, end: pos };
}

/**
 * Try to parse an `EXISTS` / `NOT EXISTS` filter (`field EXISTS` or `field NOT EXISTS`)
 * starting at `start`. Returns the equivalent Lezer token using the PRESENT_VALUE_SENTINEL
 * value (the inverse of how filterTokenToAntlr emits it) and the end offset, or null
 * if the input at that position is not an exists filter.
 *
 * `filterTokenToAntlr` emits NOT EXISTS for either `!field:=:present` *or*
 * `field:!=:present` (XOR of negation prefix and `!=` operator). Pick the
 * `!=` form on the way back so the LozengeFilter "present" toggle round-trips
 * cleanly — the operator-cell flip is the one users actually drive.
 */
function parseOneAntlrExists(antlr: string, start: number): { lezer: string; end: number } | null {
  const ANTLR_EXISTS_RE = /^([a-zA-Z0-9_.-]+)\s+(NOT\s+)?EXISTS\b/i;
  const m = ANTLR_EXISTS_RE.exec(antlr.slice(start));
  if (!m) {
    return null;
  }
  const field = m[1];
  const negated = !!m[2];
  return {
    lezer: `${field}:${negated ? '!=' : '='}:${PRESENT_VALUE_SENTINEL}`,
    end: start + m[0].length,
  };
}

// ANTLR relational prefix (snake_case) → Lezer EXPRESSION.expressionType (camelCase).
// Inverse of OBS_RELATIONAL_PREFIX in the metrics-side TraceDataPreviewTable.
const ANTLR_TO_LEZER_RELATIONAL_PREFIX: Record<string, string> = {
  any_span: 'anySpan',
  any_span2: 'anySpan2',
  any_span3: 'anySpan3',
  no_span: 'noSpan',
  span: 'span',
  child_span: 'childSpan',
  parent_span: 'parentSpan',
  root_span: 'rootSpan',
  any_session: 'anySession',
  log: 'log',
  no_log: 'noLog',
};

const ANTLR_RELATIONAL_PREFIX_RE = /^([a-zA-Z_][a-zA-Z0-9_]*)\(/;

/**
 * Quickly verify that the open / close paren counts match, ignoring any parens
 * that appear inside double-quoted strings (those are value characters, not
 * structural). Returns false on mismatch — callers bail out before parsing so
 * unbalanced input doesn't produce a half-converted Lezer query.
 */
function antlrParensBalanced(antlr: string): boolean {
  let depth = 0;
  let openQuote: string | null = null;
  for (let i = 0; i < antlr.length; i++) {
    const c = antlr[i];
    if (openQuote) {
      // Skip escaped characters inside quotes (e.g. `\"` or `\\`).
      if (c === '\\' && i + 1 < antlr.length) {
        i++;
      } else if (c === openQuote) {
        openQuote = null;
      }
      continue;
    }
    if (opensAntlrQuote(antlr, i)) {
      openQuote = c;
    } else if (c === '(') {
      depth++;
    } else if (c === ')') {
      depth--;
      if (depth < 0) {
        return false;
      }
    }
  }
  return depth === 0;
}

/**
 * Given `start` pointing just past an opening paren (depth already 1), return
 * the index just past its matching close paren. Parens inside double-quoted
 * strings are ignored (same quote/escape handling as antlrParensBalanced) so a
 * value like `field="a)b"` doesn't close the group early.
 */
function findGroupEnd(antlr: string, start: number): number {
  let depth = 1;
  let openQuote: string | null = null;
  let j = start;
  while (j < antlr.length && depth > 0) {
    const c = antlr[j];
    if (openQuote) {
      if (c === '\\' && j + 1 < antlr.length) {
        j++;
      } else if (c === openQuote) {
        openQuote = null;
      }
    } else if (opensAntlrQuote(antlr, j)) {
      openQuote = c;
    } else if (c === '(') {
      depth++;
    } else if (c === ')') {
      depth--;
    }
    j++;
  }
  return j;
}

/**
 * Convert an ANTLR query string (Observability Search format) to the Lezer
 * query string expected by the QueryBuilder. This is the reverse of the
 * metrics-side `parsedQueryToAntlr` serializer.
 */
export function antlrToLezer(antlrSource: string): string {
  const antlr = antlrSource.replace(/[\u201C\u201D]/g, '"');
  // Bail out if parens are unbalanced — the recursive descent below assumes
  // every open paren has a matching close and would otherwise emit a partial,
  // unparseable Lezer string from malformed input.
  if (!antlrParensBalanced(antlr)) {
    return '';
  }

  const parts: string[] = [];
  let i = 0;

  while (i < antlr.length) {
    // Skip whitespace between tokens.
    if (/\s/.test(antlr[i])) {
      i++;
      continue;
    }

    // Typed relational expression — `<prefix>(...)` with a known mapping.
    // Must be checked before the bare `(` handler so `any_span(...)` doesn't
    // fall through and lose its expressionType.
    const relMatch = ANTLR_RELATIONAL_PREFIX_RE.exec(antlr.slice(i));
    const lezerPrefix = relMatch ? ANTLR_TO_LEZER_RELATIONAL_PREFIX[relMatch[1].toLowerCase()] : undefined;
    if (relMatch && lezerPrefix) {
      const startInner = i + relMatch[0].length;
      const j = findGroupEnd(antlr, startInner);
      parts.push(`${lezerPrefix}(${antlrToLezer(antlr.slice(startInner, j - 1))})`);
      i = j;
      continue;
    }

    // Parenthetical expression — recursively convert the inner expression.
    if (antlr[i] === '(') {
      const j = findGroupEnd(antlr, i + 1);
      const inner = antlr.slice(i + 1, j - 1);
      const collapsed = tryCollapseDistributedMultiValue(inner);
      parts.push(collapsed ?? `(${antlrToLezer(inner)})`);
      i = j;
      continue;
    }

    // NOT <filter> — preserve the standalone keyword so it renders as its own
    // pill and can negate operators without a native inverse (for example starts with).
    if (antlr.startsWith('NOT ', i)) {
      let j = i + 4;
      while (j < antlr.length && /\s/.test(antlr[j])) {
        j++;
      }
      const membership = parseOneAntlrMembershipList(antlr, j);
      if (membership) {
        parts.push(`!${membership.lezer}`);
        i = membership.end;
        continue;
      }
      const result = parseOneAntlrFilter(antlr, j);
      if (result) {
        parts.push(`NOT ${result.lezer}`);
        i = result.end;
        continue;
      }
    }

    // AND / OR boolean keywords.
    if (antlr.startsWith('AND', i) && (i + 3 >= antlr.length || /\s/.test(antlr[i + 3]))) {
      parts.push('AND');
      i += 3;
      continue;
    }
    if (antlr.startsWith('OR', i) && (i + 2 >= antlr.length || /\s/.test(antlr[i + 2]))) {
      parts.push('OR');
      i += 2;
      continue;
    }

    // EXISTS / NOT EXISTS — inverse of filterTokenToAntlr's PRESENT_VALUE_SENTINEL handling.
    const existsResult = parseOneAntlrExists(antlr, i);
    if (existsResult) {
      parts.push(existsResult.lezer);
      i = existsResult.end;
      continue;
    }

    const membership = parseOneAntlrMembershipList(antlr, i);
    if (membership) {
      parts.push(membership.lezer);
      i = membership.end;
      continue;
    }

    // Filter token.
    const result = parseOneAntlrFilter(antlr, i);
    if (result) {
      parts.push(result.lezer);
      i = result.end;
      continue;
    }

    // Free text fallback — consume until whitespace or parenthesis.
    // Skip lezer-filter debris (`:=:""`, `field:=:`) that can ride along on
    // antlr paste; observability search syntax never uses the `:=:` delimiter.
    let j = i;
    while (j < antlr.length && !/[\s()]/.test(antlr[j])) {
      j++;
    }
    if (j > i) {
      const frag = antlr.slice(i, j);
      if (!frag.includes(':=:')) {
        parts.push(frag);
      }
    }
    i = j;
  }

  return parts.join(' ');
}
